/**
 * DjayAvatar — shared types, size scale and SVG geometry constants.
 *
 * Extracted from the former `DjayAvatar.tsx` god-file (~1619 r.) into a
 * scalable `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1).
 * This is the canonical home for the avatar's public type contract — adding a
 * new variant/accessory/emotion happens here first, then the matching render
 * branch in `parts/`.
 */
export type DjaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero'

export type DjayEmotion =
  | 'happy'
  | 'excited'
  | 'encouraging'
  | 'celebrating'
  | 'thinking'
  | 'sleeping'
  | 'pointing'
  | 'surprised'
  | 'proud'

export type DjayTier = 'rookie' | 'pro' | 'master'

export type DjayVariant =
  | 'default'
  | 'blaze'
  | 'frost'
  | 'pixel'
  | 'royal'
  | 'cosmic'
  | 'shadow'
  | 'goud'
  | 'rainbow'

export type DjayAccessory =
  | 'none'
  | 'seedling'
  | 'pencil'
  | 'badge100'
  | 'smallFlame'
  | 'trophy'
  | 'doubleFlameCrown'
  | 'starWand'
  | 'tripleStars'
  | 'scepter'
  | 'leafGarland'
  | 'medal'
  | 'cosmicOrb'
  | 'shadowOrb'
  | 'sunglasses'
  | 'cap'
  | 'goldChain'

export interface DjayAvatarProps {
  size?: DjaySize
  emotion?: DjayEmotion
  tier?: DjayTier
  variant?: DjayVariant
  accessory?: DjayAccessory
  className?: string
  animate?: boolean
  hideBody?: boolean
}

/** Per-variant colour scheme driving body/flame/arms/tail/feet/mouth. */
export interface VariantColorScheme {
  bodyGradient: [string, string, string]
  flameColors: [string, string, string, string]
  glassesFrame: string
  glassesLens: string
  armColor: string
  armHighlight: string
  tailStroke: string
  tailHighlight: string
  feetDark: string
  feetLight: string
  mouthColor: string
}

/** Rendered pixel size per named size token. */
export const SIZES: Record<DjaySize, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  hero: 128,
}

/** Canonical SVG viewBox all part-renderers draw against. */
export const DJAY_VIEWBOX = '0 0 200 220'

/**
 * Shared size thresholds — the single source of truth for which detail
 * layers are visible at a given pixel size. Used by the orchestrator to
 * derive the boolean flags passed into the part-renderers.
 */
export const DETAIL_THRESHOLD = 32
export const ARM_STUB_MIN = 24
export const MOUTH_MIN = 24
